import React, { useState } from 'react';
import {
  Dialog,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  alpha,
  CircularProgress,
  Card,
  CardContent,
  useMediaQuery,
  useTheme as useMuiTheme,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Close,
  CloudUpload,
  PhotoCamera,
  Image,
  Videocam,
  Groups,
  Photo,
  Movie,
  Delete
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import { postApi } from '../../services/postApi';
import { communityApi } from '../../services/communityApi';

import logo from '../../assets/Text-WFHCafe_WObg.png';

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (post: any) => void;
  community?: {
    id: string;
    name: string;
    slug: string;
    rules?: string[];
  };
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({
  open,
  onClose,
  onSuccess,
  community
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useCustomTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mediaType, setMediaType] = useState<'photo' | 'video' | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: ''
  });
  const [selectedCommunity, setSelectedCommunity] = useState(community?.id || '');
  const [userCommunities, setUserCommunities] = useState<any[]>([]);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldGradient = `linear-gradient(135deg, ${goldColor} 0%, #D4C690 100%)`;

  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: isMobile ? 'calc(100vw - 32px)' : (isTablet ? '600px' : '700px'),
    maxHeight: isMobile ? 'calc(100vh - 300px)' : '80vh',
    margin: 'auto',
    overflow: 'hidden',
    padding: 0,
    // Hide scrollbar but allow scrolling
    '& .MuiDialogContent-root': {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    },
  };

  // Input field styles with gold focus border
  const inputFieldStyle = {
    borderRadius: '12px',
    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.1)',
        borderWidth: '1px',
        borderRadius: '12px'
      },
      '&:hover fieldset': { 
        borderColor: goldColor,
        borderWidth: '1px'
      },
      '&.Mui-focused fieldset': { 
        borderColor: goldColor,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
      fontWeight: 600
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: goldColor,
      fontWeight: 700
    }
  };

  const gradientButtonStyle = {
    background: isDark ? goldGradient : lokiGradient,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '50px',
    fontWeight: 800,
    textTransform: 'none',
    fontSize: isMobile ? '0.9rem' : '1rem',
    padding: isMobile ? '10px 20px' : '12px 32px',
    letterSpacing: '-0.02em',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 8px 32px ${alpha(goldColor, 0.25)}`
      : `0 8px 32px ${alpha(deepGreen, 0.2)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: isDark
        ? `0 12px 40px ${alpha(goldColor, 0.35)}`
        : `0 12px 40px ${alpha(deepGreen, 0.3)}`,
      background: isDark
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, #E5D9A8 100%)`
        : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    }
  };

  // Fetch user communities when modal opens
  React.useEffect(() => {
    if (open && user) {
      fetchUserCommunities();
    }
  }, [open, user]);

  const fetchUserCommunities = async () => {
    try {
      const response = await communityApi.getUserCommunities();
      if (response.success) {
        setUserCommunities(response.communities || []);
      }
    } catch (error) {
      console.error('Failed to fetch user communities:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCameraClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCameraClose = () => {
    setAnchorEl(null);
  };

  const handleMediaTypeSelect = (type: 'photo' | 'video') => {
    setMediaType(type);
    setMediaFile(null);
    setMediaPreview(null);
    handleCameraClose();
  };

  const handleRemoveMediaSection = () => {
    setMediaType(null);
    setMediaFile(null);
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
      setMediaPreview(null);
    }
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file based on type
    if (mediaType === 'photo') {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error('Image size must be less than 20MB');
        return;
      }
    } else if (mediaType === 'video') {
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a video file');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Video size must be less than 50MB');
        return;
      }
    }

    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  const removeMedia = () => {
    setMediaFile(null);
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
      setMediaPreview(null);
    }
  };

  // In the handleSubmit function, update the postData object:
const handleSubmit = async () => {
  if (!user) {
    toast.error('Please login to create a post');
    return;
  }

  if (!formData.title.trim()) {
    toast.error('Please enter a title');
    return;
  }

  if (!mediaType && formData.content.length > 1500) {
    toast.error('Content must be 150 words or less');
    return;
  }

  if ((mediaType === 'photo' || mediaType === 'video') && !mediaFile) {
    toast.error(`Please select a ${mediaType === 'photo' ? 'photo' : 'video'} file`);
    return;
  }

  setLoading(true);

  try {
    // Map 'photo' to 'image' for backend compatibility
    const postType = mediaType === 'photo' ? 'image' : mediaType || 'text';
    
    const postData = {
      title: formData.title,
      content: formData.content, // ✅ This is already being sent
      type: postType, // ⚠️ Map 'photo' to 'image'
      communityId: selectedCommunity || undefined,
      tags: formData.tags, // ✅ This is correct - backend expects 'tags'
      media: mediaFile || undefined
    };

    console.log('📤 Sending post data:', postData); 

    const response = await postApi.createPost(postData);

    if (response.success) {
      toast.success('Post created successfully!');
      onSuccess(response.post);
      handleClose();
    } else {
      toast.error(response.error || 'Failed to create post');
    }
  } catch (error: any) {
    console.error('Create post error:', error);
    
    // Better error message
    if (error.response?.status === 400) {
      toast.error(error.response.data?.error || 'Invalid request. Check your input.');
    } else {
      toast.error(error.message || 'Failed to create post');
    }
  } finally {
    setLoading(false);
  }
};

  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      tags: ''
    });
    setSelectedCommunity(community?.id || '');
    setMediaType(null);
    setMediaFile(null);
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
      setMediaPreview(null);
    }
    setAnchorEl(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
    >
      {/* Single Content Container with Logo */}
      <Box sx={{
        p: isMobile ? 3 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Logo and Camera/Close Buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 3 : 4,
        }}>
          <Box
            component="img"
            src={logo}
            alt="WFHCafe Logo"
            sx={{
              height: isMobile ? 35 : 45,
              width: 'auto',
              filter: isDark ? 'brightness(1.1)' : 'none',
            }}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Camera Icon for Media Upload (always visible) */}
            <IconButton 
              onClick={handleCameraClick}
              sx={{ 
                color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                padding: isMobile ? '6px' : '8px',
                '&:hover': { 
                  color: goldColor,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                }
              }}
            >
              <PhotoCamera fontSize={isMobile ? "medium" : "large"} />
            </IconButton>
            
            {/* Close Button */}
            <IconButton 
              onClick={handleClose} 
              sx={{ 
                color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                padding: isMobile ? '6px' : '8px',
                '&:hover': { 
                  color: goldColor,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                }
              }}
            >
              <Close fontSize={isMobile ? "medium" : "large"} />
            </IconButton>
          </Box>
        </Box>

        {/* Camera Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCameraClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              bgcolor: isDark ? deepGreen : '#FFFFFF',
              border: `1px solid ${alpha(goldColor, 0.2)}`,
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              mt: 1,
              minWidth: '140px',
            }
          }}
        >
          <MenuItem 
            onClick={() => handleMediaTypeSelect('photo')}
            sx={{
              py: 1.5,
              color: isDark ? '#fff' : deepGreen,
              '&:hover': {
                bgcolor: alpha(goldColor, 0.1),
              }
            }}
          >
            <ListItemIcon>
              <Photo sx={{ color: goldColor }} />
            </ListItemIcon>
            <ListItemText primary="Photo" />
          </MenuItem>
          <MenuItem 
            onClick={() => handleMediaTypeSelect('video')}
            sx={{
              py: 1.5,
              color: isDark ? '#fff' : deepGreen,
              '&:hover': {
                bgcolor: alpha(goldColor, 0.1),
              }
            }}
          >
            <ListItemIcon>
              <Movie sx={{ color: goldColor }} />
            </ListItemIcon>
            <ListItemText primary="Video" />
          </MenuItem>
        </Menu>

        {/* Title */}
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          sx={{ 
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: isDark ? '#fff' : deepGreen,
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2rem',
          }}
        >
          Create New Post
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
            mb: isMobile ? 3 : 4,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}
        >
          {community ? `Posting to wfh/${community.slug}` : 'Share your thoughts with the community'}
        </Typography>

        {/* Title Input */}
        <TextField
          fullWidth
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          sx={{ mb: 3, ...inputFieldStyle }}
          placeholder="Give your post a title..."
        />

        {/* Community Select (if no community prop) */}
        {!community && userCommunities.length > 0 && (
          <FormControl fullWidth sx={{ mb: 3, ...inputFieldStyle }}>
            <InputLabel>Community (optional)</InputLabel>
            <Select
              value={selectedCommunity}
              onChange={(e) => setSelectedCommunity(e.target.value)}
              label="Community (optional)"
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '12px',
                    bgcolor: isDark ? deepGreen : '#FFFFFF',
                    border: `1px solid ${alpha(goldColor, 0.2)}`,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    '& .MuiMenuItem-root': {
                      color: isDark ? '#fff' : deepGreen,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      '&:hover': {
                        bgcolor: alpha(goldColor, 0.1),
                      },
                      '&.Mui-selected': {
                        bgcolor: alpha(goldColor, 0.2),
                        color: goldColor,
                        fontWeight: 700,
                        '&:hover': {
                          bgcolor: alpha(goldColor, 0.3),
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value="">None (Independent Post)</MenuItem>
              {userCommunities.map((comm) => (
                <MenuItem key={comm.id} value={comm.id}>
                  wfh/{comm.slug}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Content Input (shown when no media type is selected or when uploading media) */}
        {(!mediaType || (mediaType && !mediaFile)) && (
          <TextField
            fullWidth
            label="Content"
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            multiline
            rows={isMobile ? 5 : 6}
            sx={{ mb: 3, ...inputFieldStyle }}
            placeholder="What do you want to share?"
            helperText={`${formData.content.length}/1500 characters`}
            FormHelperTextProps={{
              sx: {
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
                fontSize: isMobile ? '0.8rem' : '0.85rem',
              }
            }}
          />
        )}

        {/* Media Upload Area (only shown when mediaType is selected) */}
        {mediaType && (
          <Box sx={{ mb: 3, position: 'relative' }}>
            {/* Remove Media Section Button */}
            <IconButton
              onClick={handleRemoveMediaSection}
              sx={{
                position: 'absolute',
                top: -10,
                right: -10,
                backgroundColor: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
                border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                zIndex: 10,
                width: 28,
                height: 28,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,0,0,0.3)' : 'rgba(255,0,0,0.1)',
                  color: '#ff4444',
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>

            <input
              accept={mediaType === 'photo' ? 'image/*' : 'video/*'}
              style={{ display: 'none' }}
              id="media-upload"
              type="file"
              onChange={handleMediaChange}
            />
            <label htmlFor="media-upload">
              <Box
                sx={{
                  minHeight: isMobile ? 180 : 220,
                  borderRadius: '16px',
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(5, 24, 20, 0.02)',
                  border: `2px dashed ${alpha(goldColor, 0.3)}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: goldColor,
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.04)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(goldColor, 0.1)}`,
                  }
                }}
              >
                {mediaPreview ? (
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    {mediaType === 'photo' ? (
                      <img
                        src={mediaPreview}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '16px'
                        }}
                      />
                    ) : (
                      <video
                        src={mediaPreview}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '16px'
                        }}
                        controls
                      />
                    )}
                    {/* Remove Uploaded Media Button */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMedia();
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.8)'
                        }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <CloudUpload sx={{ 
                      fontSize: isMobile ? 40 : 48, 
                      color: goldColor, 
                      mb: 2,
                      opacity: 0.8 
                    }} />
                    <Typography sx={{ 
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                      fontWeight: 800,
                      fontSize: isMobile ? '0.95rem' : '1.1rem',
                      mb: 0.5,
                      textAlign: 'center'
                    }}>
                      Upload {mediaType === 'photo' ? 'Photo' : 'Video'}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      textAlign: 'center',
                      px: 2
                    }}>
                      Click or drag and drop (max {mediaType === 'photo' ? '20MB' : '50MB'})
                    </Typography>
                  </>
                )}
              </Box>
            </label>
            
            {/* Description field for photos */}
            {/* {mediaType === 'photo' && (
              <TextField
                fullWidth
                label="Description (optional)"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                multiline
                rows={isMobile ? 2 : 3}
                sx={{ mt: 2, ...inputFieldStyle }}
                placeholder="Add a description"
              />
            )} */}
          </Box>
        )}

        {/* Tags Input */}
        <TextField
          fullWidth
          label="Tags (optional)"
          name="tags"
          value={formData.tags}
          onChange={handleInputChange}
          sx={{ mb: 3, ...inputFieldStyle }}
          placeholder="e.g., discussion, question, advice"
          helperText="Add relevant tags to help others find your post"
          FormHelperTextProps={{
            sx: {
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
            }
          }}
        />

        {/* Community Rules (if community exists) */}
        {community && community.rules && community.rules.length > 0 && (
          <Card sx={{ 
            mt: 2,
            mb: 3,
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(5, 24, 20, 0.02)',
            borderRadius: '16px',
            border: `1px solid ${alpha(goldColor, 0.1)}`
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 2.5 }}>
              <Typography variant="subtitle2" sx={{ 
                mb: 1.5, 
                fontWeight: 800,
                color: isDark ? '#fff' : deepGreen,
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                letterSpacing: '-0.02em'
              }}>
                Community Rules
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {community.rules.slice(0, 3).map((rule, index) => (
                  <Typography 
                    key={index} 
                    variant="body2" 
                    component="li" 
                    sx={{ 
                      mb: 1,
                      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                      fontSize: isMobile ? '0.85rem' : '0.9rem',
                      lineHeight: 1.3
                    }}
                  >
                    {rule}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Buttons at Bottom */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 'auto',
          pt: 3,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 2 : 0,
        }}>
          <Button
            onClick={handleClose}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontWeight: 700,
              borderRadius: '50px',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? '0.9rem' : '1rem',
              textTransform: 'none',
              minWidth: isMobile ? '120px' : 'auto',
              '&:hover': {
                bgcolor: alpha(goldColor, 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              ...gradientButtonStyle,
              minWidth: isMobile ? '120px' : 'auto',
              '&:disabled': {
                background: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                color: isDark ? 'rgba(5, 24, 20, 0.5)' : 'rgba(196, 182, 111, 0.5)',
                transform: 'none',
                boxShadow: 'none',
              }
            }}
          >
            {loading ? 'Creating...' : 'Create Post'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreatePostModal;